import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isStaleChunkError } from '../lib/stale-chunk';
import { StaleChunkRecovery } from './StaleChunkRecovery';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary that catches unhandled React render errors.
 * Displays a full-screen recovery UI rather than a white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    console.error('[ErrorBoundary]', error, errorInfo.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // A deploy invalidated a chunk this tab still references: reload onto it rather
    // than present it as a crash. The detector is shared with RouteErrorBoundary so
    // both boundaries recognise the same browser wordings.
    if (isStaleChunkError(this.state.error)) {
      return <StaleChunkRecovery className="h-dvh" />;
    }

    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg-primary px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-error/10">
            <svg className="h-8 w-8 text-error" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="mb-2 text-xl font-semibold text-text-primary">Something went wrong</h1>
          <p className="mb-6 text-sm text-text-muted">
            An unexpected error occurred. You can try reloading the page or returning to the dashboard.
          </p>
          {this.state.error && (
            <details className="mb-6 rounded-lg border border-border bg-bg-subtle p-3 text-left">
              <summary className="cursor-pointer text-xs font-medium text-text-muted">
                Error details
              </summary>
              <pre className="mt-2 overflow-auto text-xs text-error whitespace-pre-wrap">
                {this.state.error.message}
              </pre>
            </details>
          )}
          <div className="flex justify-center gap-3">
            <Button variant="secondary" onClick={this.handleGoHome}>
              Go to dashboard
            </Button>
            <Button onClick={this.handleReload}>
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
